/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { JSXInternal } from 'preact/src/jsx';
import { getEnclosingRectDims } from './utils/labelUtils';
import { styles } from './themes/AxisStyles.css';
import { AxisRedwoodTheme } from './themes/redwood/AxisTheme';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { AxisStyles } from './themes/AxisStyles.css';
import { classNames } from '../utils/UNSAFE_classNames';
import { getClientHints } from '../utils/PRIVATE_clientHints';

type SVGTextAttrs = JSXInternal.HTMLAttributes<SVGTextElement>;
type TickLabelInfo = {
  label: string;
  labelProps: { x: number; y: number; height: number; width: number };
  index: number;
};

type TextProps = {
  text: string;
  style?: SVGTextAttrs['style'];
  textProps?: SVGTextAttrs & {
    'data-oj-object'?: string;
    'data-oj-group-index'?: string | number;
  };
  isInteractive?: boolean;
  id?: string;
  isTitle?: boolean;
};

type Props = {
  title?: string;
  titleStyle?: SVGTextAttrs['style'];
  titleProps?: SVGTextAttrs;
  tickLabels: TickLabelInfo[];
  tickLabelStyle?: SVGTextAttrs['style'];
  focusedGroupIndex?: number;
  isTextInteractive?: (index: number) => boolean;
  activeId?: string;
  axisPosition: 'left' | 'right' | 'top' | 'bottom';
};

/**
 * Renders the svg text with given style and svg attributes.
 */
export function SVGText({ text, style, textProps, isTitle, isInteractive, id }: TextProps) {
  const browser = getClientHints().browser;
  return (
    <text
      style={style}
      {...textProps}
      class={classNames([
        isInteractive ? styles.isInteractive : '',
        isTitle ? styles.axisTitle : styles.axisLabel,
        !isTitle && browser === 'safari' ? styles.middleBaseline : ''
      ])}
      id={id}>
      {text}
    </text>
  );
}
/**
 * Renders the chart axis.
 */
export function Axis({
  title,
  titleStyle,
  titleProps,
  tickLabels,
  tickLabelStyle,
  focusedGroupIndex,
  axisPosition,
  activeId,
  isTextInteractive
}: Props) {
  const { classes } = useComponentTheme<AxisStyles>(AxisRedwoodTheme);
  return (
    <g class={classes}>
      {title && <SVGText text={title} textProps={titleProps} style={titleStyle} isTitle />}
      {tickLabels.length > 0 && (
        <g style={tickLabelStyle}>
          {tickLabels.map(({ label, labelProps, index }: TickLabelInfo, renderedIndex: number) => {
            const isFocused = focusedGroupIndex === renderedIndex;
            return (
              <>
                {isFocused ? (
                  <rect
                    {...getEnclosingRectDims(labelProps, axisPosition)}
                    class={styles.outlineRing}></rect>
                ) : (
                  ''
                )}
                <SVGText
                  textProps={{
                    ...labelProps,
                    'data-oj-object': 'group',
                    'data-oj-group-index': index
                  }}
                  text={label}
                  isInteractive={isTextInteractive?.(index)}
                  id={isFocused ? activeId : ''}
                />
              </>
            );
          })}
        </g>
      )}
    </g>
  );
}
